import {
  queueSlotPos,
  weaponFor,
  type EventUser,
  type GameEvent,
  type TeamId,
} from '@rekrut/shared';
import type { Engine } from './engine.js';
import { WEAPON_META, fireVolleyWave, fireWeapon } from './weapons.js';
import { other, type Unit } from './state.js';

/**
 * Событие зрителя → действие в игре.
 *
 * Ни одного упоминания названий подарков: ступень определяется только числом
 * диамантов, потому что имена локализованы и меняются, а цены пересматриваются
 * (ТЗ §4.4).
 */
/**
 * Подписка движка на волны залпа. Движок не должен знать про оружие, поэтому
 * саму стрельбу он вызывает через хук, а подключается тот здесь.
 */
export function bindWeapons(engine: Engine): void {
  engine.onVolleyWave = (ownerId, team, x, y) => fireVolleyWave(engine, ownerId, team, x, y);
}

export function applyEvent(engine: Engine, ev: GameEvent): void {
  const s = engine.state;

  switch (ev.type) {
    case 'chat':
      return onChat(engine, ev);
    case 'gift':
      return onGift(engine, ev);
    case 'like_batch':
      return onLikes(engine, ev);
    case 'follow':
    case 'subscribe':
      return onSupport(engine, ev);
    case 'share':
      return onShare(engine, ev);
    case 'viewers':
      s.viewerCount = ev.viewerCount ?? s.viewerCount;
      s.likeThreshold = Math.max(
        engine.cfg.free.likeShieldBase,
        Math.round(s.viewerCount * engine.cfg.free.likeShieldPerViewer),
      );
      return;
    case 'member':
      // Вход в эфир на игру не влияет, но это самый ранний момент, когда
      // известна аватарка: заказываем её сейчас, чтобы к выходу на поле
      // она уже лежала в кэше.
      if (ev.user?.avatarUrl) engine.onAvatarNeeded?.(ev.user.userId, ev.user.avatarUrl);
      return;
    case 'system':
      return;
  }
}

/** Разбор команды вступления. Кириллическая А и латинская A эквивалентны. */
export function parseTeamCommand(comment: string): TeamId | null {
  const c = comment.trim().toUpperCase();
  if (!c) return null;
  const ch = c[0]!;
  if (ch === 'А' || ch === 'A' || ch === '1') return 'A';
  if (ch === 'Б' || ch === 'B' || ch === 'В' || ch === '2') return 'B';
  return null;
}

function onChat(engine: Engine, ev: GameEvent): void {
  const u = ev.user;
  if (!u || !ev.comment) return;
  const team = parseTeamCommand(ev.comment);
  const unit = engine.getUnit(u.userId);

  // Команда стороны: вступление или возврат в бой после гибели.
  //
  // Если зритель уже на поле или уже стоит в очереди — команда игнорируется
  // молча. Иначе повторное «A» перекидывало бы человека между сторонами, а
  // спам буквой позволял бы мельтешить в очереди.
  if (team) {
    if (unit || engine.isQueued(u.userId)) return;
    if (!engine.canJoin(u.userId)) return;
    engine.noteJoin(u.userId);
    engine.enqueue(u, team);
    return;
  }

  // Любой комментарий чинит свой шар — дешёвая петля вовлечения (§7.1).
  if (unit && engine.canComment(u.userId)) {
    engine.noteComment(u.userId);
    engine.heal(unit, engine.cfg.free.commentHeal);
    unit.showNameUntil = engine.state.now + 1200;
  }
}

/** Сторона по умолчанию для тех, кто донатит, ещё не выбрав лагерь. */
function teamFor(engine: Engine, u: EventUser): TeamId {
  const p = engine.state.participants.get(u.userId);
  if (p?.team) return p.team;
  const a = engine.countAlive('A') + engine.state.teams.A.queue.length;
  const b = engine.countAlive('B') + engine.state.teams.B.queue.length;
  return a <= b ? 'A' : 'B';
}

function onGift(engine: Engine, ev: GameEvent): void {
  const u = ev.user;
  const diamonds = ev.diamonds ?? 0;
  if (!u || diamonds <= 0) return;

  const s = engine.state;
  const team = teamFor(engine, u);
  const p = engine.participant(u.userId, u.nickname, u.avatarUrl);
  p.team = p.team ?? team;
  p.diamonds += diamonds;

  const t = s.teams[team];
  t.donations.set(u.userId, (t.donations.get(u.userId) ?? 0) + diamonds);
  t.totalDiamonds += diamonds;

  const tier = weaponFor(engine.cfg, diamonds);
  if (!tier) return;

  // Оружие вылетает из аватарки того, кто заплатил, — и только из неё.
  // Если донатер ещё не в бою, он прыгает в голову очереди, а выстрел идёт
  // от его шара в колонне очереди: он там нарисован, зритель видит, чей залп.
  //
  // Мгновенно выпускать донатера на поле нельзя: поток подарков превращается
  // в безлимитный канал подкреплений, выкосить сторону становится невозможно
  // и осада не наступает. Прогон с мгновенным входом дал матчи по 28 минут
  // против десяти.
  const unit = engine.getUnit(u.userId);

  if (!engine.canUseWeapon(u.userId)) {
    if (unit) engine.heal(unit, unit.maxHp * 0.5);
    return;
  }
  engine.noteWeaponUse(u.userId);
  engine.noteWeapon(tier.weapon);

  if (unit) {
    fireWeapon(engine, tier.weapon, unit, u.nickname);
    return;
  }

  // Стрелок из очереди: становится в голову и бьёт со своего места в колонне.
  engine.enqueue(u, team, true);
  const idx = Math.max(0, s.teams[team].queue.findIndex((e) => e.userId === u.userId));
  const pos = queueSlotPos(team, idx, engine.cfg.queue.visibleSlots);
  fireWeapon(engine, tier.weapon, makeGunner(u, team, pos.x, pos.y), u.nickname);
}

/** Временный «стрелок» для того, кто ещё в очереди: только позиция и команда. */
function makeGunner(u: EventUser, team: TeamId, x: number, y: number): Unit {
  return {
    userId: u.userId,
    nickname: u.nickname,
    avatarUrl: u.avatarUrl,
    team,
    x,
    y,
    vx: 0,
    vy: 0,
    hp: 1,
    maxHp: 1,
    radius: 1,
    level: 1,
    kills: 0,
    invulnUntil: 0,
    shieldUntil: 0,
    crownUntil: 0,
    levelUpAt: 0,
    nextShotAt: 0,
    nextTargetAt: 0,
    targetId: null,
    targetIsThrone: false,
    driftPhase: 0,
    castAt: -1e9,
    castDirX: 0,
    castDirY: 0,
    kbX: 0,
    kbY: 0,
    lastKillAt: -1e9,
    combo: 0,
    spawnedAt: 0,
    lastDamagedAt: 0,
    hitFlash: 0,
    showNameUntil: 0,
  };
}

function onLikes(engine: Engine, ev: GameEvent): void {
  const s = engine.state;
  s.likeCounter += ev.likes ?? 0;
  if (s.likeCounter < s.likeThreshold) return;
  s.likeCounter = 0;

  // Щит достаётся стороне, чей трон ближе к падению: лайки — рычаг неплатящих,
  // и он должен работать против снежного кома.
  const weakest: TeamId = s.thrones.A.hp <= s.thrones.B.hp ? 'A' : 'B';
  s.teams[weakest].shieldUntil = s.now + engine.cfg.free.likeShieldSec * 1000;
  engine.banner('❤ ЩИТ ЗА ЛАЙКИ', s.teams[weakest].label, s.teams[weakest].color, 1600);
}

function onSupport(engine: Engine, ev: GameEvent): void {
  const u = ev.user;
  if (!u) return;
  const unit = engine.getUnit(u.userId);
  const cfg = engine.cfg.free;
  if (unit) {
    engine.heal(unit, ev.type === 'subscribe' ? cfg.subscribeHeal : cfg.followHeal);
    if (ev.type === 'subscribe') unit.crownUntil = engine.state.now + cfg.crownSec * 1000;
    unit.showNameUntil = engine.state.now + 2000;
  } else {
    engine.enqueue(u, teamFor(engine, u), ev.type === 'subscribe');
  }
  engine.tickerPush(
    ev.type === 'subscribe' ? `@${u.nickname} оформил подписку 👑` : `@${u.nickname} подписался`,
    0xffd25e,
    2000,
  );
}

/** Репост двигает в голову очереди — бесплатный аналог первой ступени. */
function onShare(engine: Engine, ev: GameEvent): void {
  const u = ev.user;
  if (!u || engine.getUnit(u.userId)) return;
  engine.enqueue(u, teamFor(engine, u), true);
  engine.tickerPush(`@${u.nickname} сделал репост — вперёд очереди`, 0x7dffb0, 2000);
}

export { other };
